import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const GoalHorizonSection = ({ 
  title, 
  icon, 
  goals, 
  onGoalToggle, 
  onGoalAdd, 
  onGoalRemove, 
  onGoalEdit,
  isExpanded,
  onToggleExpanded 
}) => {
  const [newGoalText, setNewGoalText] = useState('');
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editText, setEditText] = useState('');

  const handleAddGoal = () => {
    if (newGoalText?.trim()) {
      onGoalAdd(newGoalText?.trim());
      setNewGoalText('');
    }
  };

  const handleStartEdit = (goal) => {
    setEditingGoalId(goal?.id);
    setEditText(goal?.text);
  };

  const handleSaveEdit = () => {
    if (editText?.trim()) {
      onGoalEdit(editingGoalId, editText?.trim());
    }
    setEditingGoalId(null);
    setEditText('');
  };

  const handleCancelEdit = () => {
    setEditingGoalId(null);
    setEditText('');
  };

  const handleRemoveGoal = (goalId, goalText) => {
    if (window.confirm(`Are you sure you want to remove "${goalText}"?`)) {
      onGoalRemove(goalId);
    }
  };

  const completedCount = goals?.filter(goal => goal?.completed)?.length;

  // Function to add bulk remove functionality for completed goals
  const handleRemoveCompleted = () => {
    const completedGoals = goals?.filter(goal => goal?.completed);
    if (completedGoals?.length === 0) return;
    
    if (window.confirm(`Are you sure you want to remove ${completedGoals?.length} completed goal${completedGoals?.length > 1 ? 's' : ''}?`)) {
      completedGoals?.forEach(goal => onGoalRemove(goal?.id));
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-gentle mb-4">
      {/* Section Header */}
      <button
        onClick={onToggleExpanded}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors duration-200"
      >
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-8 h-8 bg-secondary rounded-lg">
            <Icon name={icon} size={16} color="white" />
          </div>
          <div className="text-left">
            <h3 className="font-heading font-semibold text-foreground">
              {title} Goals
            </h3>
            <p className="text-sm text-muted-foreground">
              {completedCount} of {goals?.length} completed
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {goals?.length > 0 && (
            <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-success transition-all duration-300"
                style={{ width: goals?.length > 0 ? `${(completedCount / goals?.length) * 100}%` : '0%' }}
              />
            </div>
          )}
          <Icon 
            name={isExpanded ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-muted-foreground"
          />
        </div>
      </button>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Bulk Actions - Show only when there are completed goals */}
          {completedCount > 0 && (
            <div className="flex items-center justify-between p-2 bg-muted/20 rounded-lg border-l-4 border-l-success">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-sm text-foreground">
                  {completedCount} completed goal{completedCount > 1 ? 's' : ''}
                </span>
              </div>
              <Button
                variant="ghost"
                size="xs"
                onClick={handleRemoveCompleted}
                iconName="Trash2"
                iconSize={14}
                className="text-muted-foreground hover:text-error hover:bg-error/10"
              >
                Clear Completed
              </Button>
            </div>
          )}

          {/* Goal List */}
          {goals?.length > 0 && (
            <div className="space-y-2">
              {goals?.map((goal) => (
                <div key={goal?.id} className="group flex items-start space-x-3 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors duration-200">
                  <Checkbox
                    checked={goal?.completed}
                    onChange={(e) => onGoalToggle(goal?.id, e?.target?.checked)}
                    className="mt-0.5 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    {editingGoalId === goal?.id ? (
                      <div className="space-y-2">
                        <Input
                          type="text"
                          value={editText}
                          onChange={(e) => setEditText(e?.target?.value)}
                          placeholder="Edit goal..."
                          className="text-sm"
                          autoFocus
                        />
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="default"
                            size="xs"
                            onClick={handleSaveEdit}
                            iconName="Check"
                            iconSize={14}
                          >
                            Save
                          </Button>
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={handleCancelEdit}
                            iconName="X"
                            iconSize={14}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between min-h-[20px]">
                        <p className={`text-sm flex-1 pr-2 ${goal?.completed ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                          {goal?.text}
                        </p>
                        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity duration-200 flex-shrink-0">
                          <button
                            onClick={() => handleStartEdit(goal)}
                            className="p-1 text-muted-foreground hover:text-foreground hover:bg-background rounded transition-colors duration-200"
                            title="Edit goal"
                          >
                            <Icon name="Edit2" size={14} />
                          </button>
                          <button
                            onClick={() => handleRemoveGoal(goal?.id, goal?.text)}
                            className="p-1 text-muted-foreground hover:text-error hover:bg-error/10 rounded transition-colors duration-200"
                            title="Remove goal"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add New Goal */}
          <div className="flex items-center space-x-2">
            <div className="flex-1">
              <Input
                type="text"
                value={newGoalText}
                onChange={(e) => setNewGoalText(e?.target?.value)}
                placeholder={`Add a new ${title?.toLowerCase()} goal...`}
                onKeyPress={(e) => e?.key === 'Enter' && handleAddGoal()}
              />
            </div>
            <Button
              variant="outline"
              size="default"
              onClick={handleAddGoal}
              disabled={!newGoalText?.trim()}
              iconName="Plus"
              iconSize={16}
            >
              Add
            </Button>
          </div>

          {goals?.length === 0 && (
            <div className="text-center py-6 text-muted-foreground">
              <Icon name="Target" size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No {title?.toLowerCase()} goals yet</p>
              <p className="text-xs">Add your first goal above</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GoalHorizonSection;